import { useMemo, useState } from "react";
import { movePermitDate } from "../../api/permit";
import {
  formatPermitCoverLabel,
  listPunchPermits,
} from "../../utils/punchPermitCover";
import "./ConvertAttendanceToPermitModal.css";

function getApiErrorMessage(err, fallback) {
  const message = err?.response?.data?.message;
  if (Array.isArray(message)) return message.join("\n");
  if (typeof message === "string" && message.trim()) return message;
  return err?.message || fallback;
}

function formatHistoryDate(value) {
  if (!value) return "—";
  const iso = String(value).slice(0, 10);
  const [year, month, day] = iso.split("-").map(Number);
  if (!year || !month || !day) return iso;
  return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;
}

export default function MovePermitDateModal({
  employeeName,
  fromDate,
  permits,
  onClose,
  onMoved,
}) {
  const movable = useMemo(
    () => listPunchPermits(permits).filter((permit) => permit?.id),
    [permits],
  );
  const [permitId, setPermitId] = useState(movable[0]?.id || "");
  const [newDate, setNewDate] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const selected = movable.find((permit) => permit.id === permitId) || null;

  async function handleSubmit(event) {
    event.preventDefault();
    if (!permitId || !newDate) {
      setError("اختر الإذن والتاريخ الصحيح");
      return;
    }
    if (newDate === String(fromDate).slice(0, 10)) {
      setError("التاريخ الجديد يجب أن يختلف عن تاريخ الإذن الحالي");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const result = await movePermitDate(permitId, {
        newDate,
        reason: reason.trim() || undefined,
      });
      onMoved?.(result, { fromDate, newDate, permitId });
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر نقل تاريخ الإذن"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="cap-overlay"
      role="presentation"
      onClick={() => {
        if (!submitting) onClose?.();
      }}
    >
      <form
        className="cap-modal"
        dir="rtl"
        onClick={(event) => event.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <h3 className="cap-title">نقل الإذن ليوم آخر</h3>
        <p className="cap-subtitle">
          {employeeName ? `${employeeName} · ` : ""}
          من {formatHistoryDate(fromDate)}
        </p>

        {!movable.length ? (
          <div className="cap-alert error" role="alert">
            لا يوجد إذن يمكن نقله في هذا اليوم
          </div>
        ) : (
          <>
            <label className="cap-reason">
              <span>الإذن</span>
              <select
                value={permitId}
                onChange={(event) => setPermitId(event.target.value)}
                disabled={submitting}
              >
                {movable.map((permit) => (
                  <option key={permit.id} value={permit.id}>
                    {formatPermitCoverLabel(permit)}
                  </option>
                ))}
              </select>
            </label>

            {selected ? (
              <dl className="cap-stats">
                <div>
                  <dt>الوقت الحالي</dt>
                  <dd>
                    {selected.startTime || "—"} – {selected.endTime || "—"}
                  </dd>
                </div>
                {selected.hours != null ? (
                  <div>
                    <dt>الساعات</dt>
                    <dd>{selected.hours}</dd>
                  </div>
                ) : null}
              </dl>
            ) : null}

            <label className="cap-reason">
              <span>التاريخ الصحيح</span>
              <input
                type="date"
                value={newDate}
                onChange={(event) => setNewDate(event.target.value)}
                disabled={submitting}
                required
              />
            </label>

            <label className="cap-reason">
              <span>سبب التصحيح (اختياري)</span>
              <input
                type="text"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                disabled={submitting}
                maxLength={500}
                placeholder="مثال: الموظف قدّم الإذن على يوم بالغلط"
              />
            </label>

            <p className="cap-hint">
              سيتم شيل تغطية الإذن من اليوم الغلط وربطها باليوم الصح، مع إعادة
              احتساب جزاءات التأخير/الانصراف المبكر على اليومين.
            </p>
          </>
        )}

        {error ? (
          <div className="cap-alert error" role="alert">
            {error}
          </div>
        ) : null}

        <div className="cap-actions">
          <button
            type="button"
            className="cap-cancel"
            onClick={onClose}
            disabled={submitting}
          >
            إلغاء
          </button>
          <button
            type="submit"
            className="cap-submit"
            disabled={submitting || !movable.length || !permitId || !newDate}
          >
            {submitting ? "جاري النقل..." : "نقل الإذن"}
          </button>
        </div>
      </form>
    </div>
  );
}
